/**
 * DaysDoneRing — the W3 fix (direction §2).
 *
 * A mileage-independent completion signal: one segment per day in
 * `weekPlan.days`, filled when that day is completed regardless of its
 * mileage. **Completing any non-rest day advances it — strength/mobility
 * included** — which is the gap W3 flagged (the weekly mileage tally
 * moves nothing on a 0-mi day). Rest days render as a quiet fill so they
 * read as non-sessions, not as incomplete work.
 *
 * This is pure presentation of the existing `weekPlan.days` array — no
 * new data contract. `Day.completionStatus` + `Day.rest` already exist.
 *
 * Built in PR 1; mounted into the loop hero's RewardStrip. The reward
 * beat (PR 2) targets today's segment via `[data-seg="today"]`.
 */
import type { Day } from '../../fixtures/plan';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './DaysDoneRing.module.css';

/** A day with no mileage is a rest day (matches WeekPlan's own logic). */
export function isRestDay(d: Day): boolean {
  return d.rest ?? d.mileage === '—';
}

/**
 * Count sessions (non-rest days) and how many are completed. Single-
 * sourced so the ring label and the WeekPlan header counter never drift.
 */
export function sessionCounts(days: Day[]): { completed: number; sessions: number } {
  const sessionDays = days.filter((d) => !isRestDay(d));
  return {
    completed: sessionDays.filter((d) => d.completionStatus === 'completed').length,
    sessions: sessionDays.length,
  };
}

type SegState = 'on' | 'today' | 'rest' | 'off' | 'empty';

/** Number of hollow placeholder segments shown in the empty / first-run
 *  state (a full training week) — teaches the loop without fake data. */
const EMPTY_PLACEHOLDER_SEGMENTS = 7;

export interface DaysDoneRingProps {
  days: Day[];
  /** Ring only, no "{n} of {m} sessions" text (e.g. when the parent
   *  renders its own label). Defaults to showing the label. */
  hideLabel?: boolean;
  /** Completion beat — pop today's segment as it fills (the W3 reward).
   *  Reduced motion drops the pop, keeps the fill. */
  celebrating?: boolean;
  className?: string;
}

export function DaysDoneRing({ days, hideLabel, celebrating, className }: DaysDoneRingProps) {
  const iconsOn = useIconSystem();
  const { completed, sessions } = sessionCounts(days);
  const isEmpty = days.length === 0;
  // CD-ICONS-02 · R2 — `chip.reward` finally has a mount, and it is THIS: the
  // ring's TERMINAL state. Every session in the week is done.
  //
  // ⚠️ TERMINAL MEANS EARNED, NOT "NEARLY". The token renders only when
  // `completed === sessions` with at least one session to complete — never at
  // 4-of-5, never on an empty week. That is the fill vocabulary being honest:
  // a solid star means EARNED, and showing it early would spend the word.
  //
  // This closes BUILD-NOTES §1.3. The lane declined to guess this mount twice —
  // "on track" is not "earned", and painting the RewardStrip tick as a token
  // would have muddied the grammar. R2 answered it; RewardStrip is unchanged.
  const weekComplete = sessions > 0 && completed === sessions;

  const segments: { key: string; state: SegState; cls: string }[] = isEmpty
    ? Array.from({ length: EMPTY_PLACEHOLDER_SEGMENTS }, (_, i) => ({
        key: `placeholder-${i}`,
        state: 'empty' as const,
        cls: styles.seg,
      }))
    : days.map((d, i) => {
        const rest = isRestDay(d);
        const done = d.completionStatus === 'completed';
        let state: SegState;
        if (rest) state = 'rest';
        else if (done) state = 'on';
        else if (d.today) state = 'today';
        else state = 'off';

        const cls = [
          styles.seg,
          state === 'on' ? styles.isOn : '',
          state === 'today' ? styles.isToday : '',
          state === 'rest' ? styles.isRest : '',
          // Pop today's segment as it fills during the completion beat.
          celebrating && d.today ? styles.pop : '',
        ]
          .filter(Boolean)
          .join(' ');

        return { key: `${d.dayShort}-${i}`, state, cls };
      });

  return (
    <div className={[styles.daysDone, className].filter(Boolean).join(' ')}>
      <span className={styles.ring} aria-hidden="true">
        {segments.map((s) => (
          <span key={s.key} className={s.cls} data-seg={s.state} />
        ))}
      </span>
      {iconsOn && weekComplete && (
        <span className={styles.earned} aria-hidden="true">
          <Icon name="chip.reward" size={16} />
        </span>
      )}
      {!hideLabel && (
        <span className={styles.label}>
          <b>{completed}</b> of {sessions} sessions
        </span>
      )}
    </div>
  );
}
